import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import en from './locales/en.json'
import uk from './locales/uk.json'
import { DEFAULT_LOCALE, LOCALES, type Locale } from '@/lib/types'

/**
 * Both languages are bundled rather than fetched.
 *
 * The translation files are a few kilobytes, and loading them over the network
 * would mean the first paint either waits for a request or flashes untranslated
 * text — a poor trade for the bytes saved. It also keeps prerendering simple:
 * the build has every string it needs without a network call.
 */
export const resources = {
  uk: { translation: uk },
  en: { translation: en },
} as const

void i18n.use(initReactI18next).init({
  resources,
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: [...LOCALES],
  interpolation: {
    // React escapes for us; doing it twice mangles apostrophes in Ukrainian.
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
})

export default i18n

/**
 * Picks the language for a first-time visitor from their browser settings.
 * The URL always wins over this — it is only the starting point for someone
 * arriving at the bare domain.
 */
export function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE

  for (const candidate of navigator.languages ?? [navigator.language]) {
    const base = candidate.split('-')[0]?.toLowerCase()
    if (base && (LOCALES as readonly string[]).includes(base)) {
      return base as Locale
    }
    // Ukrainian speakers frequently have a Russian browser locale set; showing
    // them Ukrainian is a better guess than English.
    if (base === 'ru') return 'uk'
  }
  return DEFAULT_LOCALE
}
